import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2Icon } from 'lucide-react'
import { memo, type ReactNode, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { getAppBaseUrl } from '@/app.const'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

import { AdminOnlyNotice } from './admin-only-notice.atom'
import type { OrgSlugSectionProps } from './org-settings.interface'
import { type OrgSlugFormSchemaType, orgSlugFormSchema } from './org-settings.schema'
import { SlugSuggestions } from './slug-suggestions.atom'
import { useSlugConflict } from './use-slug-conflict.hook'

const OrgSlugSectionBase: React.FC<OrgSlugSectionProps> = ({
  slug,
  onSlugChange,
  isLoading = false,
  isDisabled = false,
  className,
}): ReactNode => {
  const form = useForm<OrgSlugFormSchemaType>({
    resolver: zodResolver(orgSlugFormSchema),
    defaultValues: { slug },
    mode: 'onChange',
  })
  const { suggestions, handleConflictError, resetSuggestions, selectSuggestion } = useSlugConflict({
    onError: (msg) => form.setError('slug', { message: msg }),
    onClearError: () => form.clearErrors('slug'),
    onApplySlug: (s) => form.setValue('slug', s, { shouldValidate: true, shouldDirty: true }),
  })

  useEffect(() => {
    form.reset({ slug })
  }, [slug, form])

  const handleSubmit = async (data: OrgSlugFormSchemaType): Promise<void> => {
    try {
      resetSuggestions()
      await onSlugChange(data.slug)
    } catch (error) {
      if (handleConflictError(error)) return
      throw error
    }
  }

  return (
    <section className={cn('org-slug-section', className)}>
      <OrgSlugHeader />
      {isDisabled && <AdminOnlyNotice />}
      <OrgSlugFormContent
        form={form}
        isLoading={isLoading}
        isDisabled={isDisabled}
        onSubmit={handleSubmit}
        suggestions={suggestions}
        onSuggestionSelect={selectSuggestion}
      />
    </section>
  )
}

const OrgSlugHeader: React.FC = () => (
  <div className="org-slug-header mb-4">
    <h3 className="text-lg font-medium">Organization URL</h3>
    <p className="text-sm text-muted-foreground">
      Customize your organization&apos;s URL. This will be used in all links to your organization.
    </p>
  </div>
)

interface OrgSlugFormContentProps {
  form: ReturnType<typeof useForm<OrgSlugFormSchemaType>>
  isLoading: boolean
  isDisabled: boolean
  onSubmit: (data: OrgSlugFormSchemaType) => void
  suggestions: string[]
  onSuggestionSelect: (slug: string) => void
}

const OrgSlugFormContent: React.FC<OrgSlugFormContentProps> = ({
  form,
  isLoading,
  isDisabled,
  onSubmit,
  suggestions,
  onSuggestionSelect,
}) => {
  const { isDirty, isValid } = form.formState
  const currentSlug = form.watch('slug')
  const effectiveDisabled = isLoading || isDisabled

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="org-slug-form space-y-4">
        <OrgSlugFormField
          control={form.control}
          isDisabled={effectiveDisabled}
          currentSlug={currentSlug}
          suggestions={suggestions}
          onSuggestionSelect={onSuggestionSelect}
        />
        {!isDisabled && isDirty && <OrgSlugSaveButton isValid={isValid} isLoading={isLoading} />}
      </form>
    </Form>
  )
}

interface OrgSlugFormFieldProps {
  control: ReturnType<typeof useForm<OrgSlugFormSchemaType>>['control']
  isDisabled: boolean
  currentSlug: string
  suggestions: string[]
  onSuggestionSelect: (slug: string) => void
}

const OrgSlugFormField: React.FC<OrgSlugFormFieldProps> = ({
  control,
  isDisabled,
  currentSlug,
  suggestions,
  onSuggestionSelect,
}) => (
  <FormField
    control={control}
    name="slug"
    render={({ field }) => (
      <FormItem>
        <FormLabel>URL Slug</FormLabel>
        <FormControl>
          <div className="org-slug-input-wrapper flex items-center gap-2">
            <span className="text-sm text-muted-foreground">{getAppBaseUrl()}/</span>
            <Input
              {...field}
              placeholder="your-organization"
              disabled={isDisabled}
              className="org-slug-input max-w-xs font-mono lowercase"
              onChange={(e) => field.onChange(e.target.value.toLowerCase())}
            />
          </div>
        </FormControl>
        <FormDescription>
          {currentSlug ? (
            <span className="org-slug-preview font-mono text-xs">
              {getAppBaseUrl()}/{currentSlug}
            </span>
          ) : (
            'Enter a URL slug for your organization'
          )}
        </FormDescription>
        <FormMessage />
        {suggestions.length > 0 && (
          <SlugSuggestions suggestions={suggestions} onSelect={onSuggestionSelect} />
        )}
      </FormItem>
    )}
  />
)

interface OrgSlugSaveButtonProps {
  isValid: boolean
  isLoading: boolean
}

const OrgSlugSaveButton: React.FC<OrgSlugSaveButtonProps> = ({ isValid, isLoading }) => (
  <Button type="submit" disabled={!isValid || isLoading} className="org-slug-save-button">
    {isLoading ? (
      <>
        <Loader2Icon className="mr-2 size-4 animate-spin" />
        Saving...
      </>
    ) : (
      'Save URL'
    )}
  </Button>
)

export const OrgSlugSection = memo(OrgSlugSectionBase)

OrgSlugSection.displayName = 'OrgSlugSection'
